import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle,
  XCircle,
  FileCheck,
  Building2,
  MapPin,
  Mail,
  Phone,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { format, parseISO, isAfter } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteApproval = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Approval form
  const [signedName, setSignedName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Rejection form
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Success/completed states
  const [completed, setCompleted] = useState(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/quotes/approval/${token}`);
        setQuote(response.data);
        
        // Check if already processed
        if (['approved', 'rejected', 'converted', 'expired'].includes(response.data.status)) {
          setCompleted(response.data.status);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Quote not found or link expired');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [token]);

  const handleApprove = async () => {
    if (!signedName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/quotes/approval/${token}/approve`, {
        signed_name: signedName
      });
      setCompleted('approved');
      toast.success('Quote approved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/quotes/approval/${token}/reject`, {
        reason: rejectionReason
      });
      setCompleted('rejected');
      toast.success('Quote rejected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject quote');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (value) => {
    const symbol = quote?.currency_symbol || '$';
    return `${symbol}${new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0)}`;
  };

  const isExpired = () => {
    if (!quote?.valid_until) return false;
    return isAfter(new Date(), parseISO(quote.valid_until));
  };

  // Force dark mode on this page
  useEffect(() => {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
    return () => {};
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading quote...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Quote Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {completed === 'approved' ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
                <h2 className="text-2xl font-bold mb-2">Quote Approved!</h2>
                <p className="text-muted-foreground mb-4">
                  Thank you for approving quote {quote?.quote_number}. 
                  The team will be in touch shortly to schedule your work.
                </p>
              </>
            ) : completed === 'rejected' ? (
              <>
                <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
                <h2 className="text-2xl font-bold mb-2">Quote Rejected</h2>
                <p className="text-muted-foreground mb-4">
                  Quote {quote?.quote_number} has been rejected.
                  If you have any questions, please contact us.
                </p>
              </>
            ) : completed === 'expired' ? (
              <>
                <Clock className="h-16 w-16 mx-auto mb-4 text-orange-500" />
                <h2 className="text-2xl font-bold mb-2">Quote Expired</h2>
                <p className="text-muted-foreground mb-4">
                  This quote has expired. Please contact us for a new quote.
                </p>
              </>
            ) : (
              <>
                <FileCheck className="h-16 w-16 mx-auto mb-4 text-blue-500" />
                <h2 className="text-2xl font-bold mb-2">Quote Already Processed</h2>
                <p className="text-muted-foreground mb-4">
                  This quote has already been {completed}.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#ffd108] mb-2">EQUIPR CORE</h1>
          <p className="text-muted-foreground">Quote Review & Approval</p>
        </div>

        {/* Company Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">{quote?.company?.name}</h2>
                {quote?.company?.address && (
                  <p className="text-muted-foreground text-sm">{quote.company.address}</p>
                )}
                {quote?.company?.phone && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1 mt-1">
                    <Phone className="h-3 w-3" /> {quote.company.phone}
                  </p>
                )}
                {quote?.company?.email && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {quote.company.email}
                  </p>
                )}
                {quote?.company?.abn && (
                  <p className="text-muted-foreground text-sm mt-1">ABN: {quote.company.abn}</p>
                )}
              </div>
              <div className="text-right">
                <Badge className="bg-[#ffd108] text-black text-lg px-4 py-1 font-mono">
                  {quote?.quote_number}
                </Badge>
                <p className="text-muted-foreground text-sm mt-2">
                  Valid until: {quote?.valid_until ? format(parseISO(quote.valid_until), 'dd MMM yyyy') : '-'}
                </p>
                {isExpired() && (
                  <Badge variant="destructive" className="mt-2">EXPIRED</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote To */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Quote To
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-medium">{quote?.customer?.company_name}</p>
                {quote?.customer?.contact_name && (
                  <p className="text-muted-foreground text-sm">{quote.customer.contact_name}</p>
                )}
              </div>
              {quote?.site && (
                <div>
                  <p className="text-muted-foreground text-sm flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Site
                  </p>
                  <p className="font-medium">{quote.site.name || quote.site.address}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle>Quote Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quote?.line_items?.map((item, index) => (
                  <TableRow key={`line-${index}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.description}</p>
                        <p className="text-xs text-muted-foreground capitalize">{item.item_type}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                    <TableCell className="capitalize">{item.unit}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(item.rate)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatCurrency(item.quantity * item.rate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Totals */}
            <div className="p-4 border-t">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  {quote?.fuel_levy_amount > 0 && !(quote?.line_items || []).some(li => li.item_type === 'fuel_levy') && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Fuel Levy ({quote?.fuel_levy_rate}%)</span>
                      <span className="font-mono">{formatCurrency(quote?.fuel_levy_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">{formatCurrency(quote?.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{quote?.tax_name || 'GST'} ({quote?.tax_rate || 10}%)</span>
                    <span className="font-mono">{formatCurrency(quote?.tax_amount)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-mono font-bold text-xl text-[#ffd108]">
                      {formatCurrency(quote?.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Terms & Conditions */}
        {quote?.terms_and_conditions && (
          <Card>
            <CardHeader>
              <CardTitle>Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                {quote.terms_and_conditions}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Approval Section */}
        {!isExpired() && (
          <Card className="border-[#ffd108]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-[#ffd108]" />
                Approve or Reject Quote
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {!showRejectForm ? (
                <>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signedName">Your Name (Signature) <span className="text-red-500">*</span></Label>
                      <Input
                        id="signedName"
                        value={signedName}
                        onChange={(e) => setSignedName(e.target.value)}
                        placeholder="Type your full name"
                        className="text-lg"
                        data-testid="signature-name-input"
                      />
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="terms"
                        checked={agreedToTerms}
                        onCheckedChange={setAgreedToTerms}
                        data-testid="agree-terms-checkbox"
                      />
                      <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                        I have read and agree to the terms and conditions above. 
                        I authorize the work to be performed as quoted.
                      </Label>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleApprove}
                      disabled={processing || !signedName || !agreedToTerms}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="approve-quote-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {processing ? 'Processing...' : 'Approve Quote'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectForm(true)}
                      disabled={processing}
                      className="flex-1 border-red-500 text-red-500 hover:bg-red-500/10"
                      data-testid="show-reject-form-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject Quote
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="rejectionReason">Reason for Rejection (Optional)</Label>
                    <Textarea
                      id="rejectionReason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please let us know why you're rejecting this quote..."
                      rows={3}
                      data-testid="rejection-reason-input"
                    />
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="destructive"
                      onClick={handleReject}
                      disabled={processing}
                      className="flex-1"
                      data-testid="confirm-reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {processing ? 'Processing...' : 'Confirm Rejection'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectForm(false)}
                      disabled={processing}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Expired Warning */}
        {isExpired() && (
          <Card className="border-orange-500">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-orange-500" />
              <h3 className="text-lg font-bold mb-2">This Quote Has Expired</h3>
              <p className="text-muted-foreground">
                This quote was valid until {quote?.valid_until ? format(parseISO(quote.valid_until), 'dd MMM yyyy') : '-'}.
                Please contact us for an updated quote.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-muted-foreground text-sm py-4">
          <p>Powered by EQUIPR CORE</p>
        </div>
      </div>
    </div>
  );
};

export default QuoteApproval;
